import { lazy, Suspense, useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Space, Table, Row, Col, Typography, Tooltip, Card, theme, Select, Dropdown, TreeSelect, Modal, message, Spin, Tag, Tree } from 'antd';
import dayjs from 'dayjs';
import config from '@/config';
import './OrganizationList.less';
import { TABLE_PAGE_SIZE_OPTIONS, computeButtonColLayout, filterFormItemLayout, filterFormGridLayout, filterFormItemStyle } from '@utils/property';
import { conferenceActions } from '../store/conferenceSlice';
import { sponsorList, getSponsorFilterOptions, conferenceOnceApi, conferenceUpdateHallPlanImagesApi, delSponsor } from '@services/Conference';
import AddOrganizationDrawer from './components/AddOrganization';
import BenefitRealizationModal from './components/BenefitRealizationModal';
import HallPlanImageModal from './components/HallPlanImageModal';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { CONFERENCE_WORKSPACE_PARENT_PATH, CONFERENCE_WORKSPACE_ROUTE_BASE } from '@/layouts/conferenceWorkspaceConfig';
import { workspaceConferenceInfoPath } from '@/layouts/conferenceWorkspacePaths';
import BenefitManageDrawer from './components/BenefitManageDrawer';
import { BENEFIT_CATEGORIES, BENEFIT_CATEGORY_MAP } from './constants';

/** 动态加载，避免机构权益列表首屏打包机构详情 Drawer */
const ViewOrgDrawer = lazy(() => import('@/components/DC/components/ViewOrgDrawer'));

const { Text } = Typography;
const { setGuestFilter: setFilter } = conferenceActions;

/** 权益分类下拉选项（由 constants 的 BENEFIT_CATEGORIES 统一派生，value/label 格式） */
const BENEFIT_TYPE_OPTIONS = BENEFIT_CATEGORIES.map(({ id, name }) => ({ value: id, label: name }));

/** 排序选项 */
const SORT_OPTIONS = [
    { key: '1', label: '按添加时间倒序（默认）' },
    { key: '2', label: '按合同金额倒序' },
    { key: '3', label: '按机构名义倒序' },
];

/** 渲染单条权益内容（适配接口 benefitList 数据结构） */
function BenefitContent({ items }) {
    if (!items || items.length === 0) return <Text type="secondary">&ndash;</Text>;

    return (
        <ul className="benefit-list">
            {items.map((item, idx) => {
                // 展位权益展示展位号
                let boothText = '';
                if (item.isBoothBenefit === 1 && item.boothNumbers) {
                    try {
                        const booths = JSON.parse(item.boothNumbers);
                        boothText = Array.isArray(booths) ? booths.join('、') : item.boothNumbers;
                    } catch {
                        boothText = item.boothNumbers;
                    }
                }

                // 权益名称（黑色加粗）+ 数量/展位号（黑色加粗）+ 权益描述（黑色）
                const metaText = item.isBoothBenefit === 1 ? `（数量:${item.quantity}，展位号：${boothText || '–'}）` : `（数量:${item.quantity}）`;

                const key = `${item.benefitName}-${idx}`;

                return (
                    <li key={key} className="benefit-item">
                        <span className="benefit-name">{item.benefitName}</span>
                        <span className="benefit-meta">{metaText}</span>
                        <span className="benefit-desc">：{item.remark || <Text type="secondary">&ndash;</Text>}</span>
                    </li>
                );
            })}
        </ul>
    );
}

/** 格式化日期为 xxxx年x月x日 */
function formatForumDate(dateStr) {
    if (!dateStr) return '';
    const d = dayjs(dateStr);
    if (!d.isValid()) return dateStr;
    return d.format('YYYY年M月D日');
}

/** 渲染论坛权益（按 hallsId 分组，黄色日期 + 黑色会场名 + 权益列表） */
function ForumBenefitContent({ items }) {
    if (!items || items.length === 0) return <Text type="secondary">&ndash;</Text>;

    // 按 hallsId 分组
    const groups = items.reduce((acc, item) => {
        const key = item.hallsId || 0;
        if (!acc[key]) {
            acc[key] = {
                hallName: item.hallName || '',
                dates: item.dates || '',
                benefits: [],
            };
        }
        acc[key].benefits.push(item);
        return acc;
    }, {});

    return (
        <div className="forum-benefit-list">
            {Object.entries(groups).map(([hallsId, group]) => (
                <div key={hallsId} className="forum-group">
                    {group.hallName && (
                        <div className="forum-hall-name">
                            {group.dates && <span className="forum-date">{formatForumDate(group.dates)}</span>}
                            {group.dates && group.hallName && <span className="forum-date-sep"> </span>}
                            <span className="forum-hall-text">{group.hallName}</span>
                        </div>
                    )}
                    <ul className="benefit-list">
                        {group.benefits.map((item, idx) => {
                            const key = `${item.benefitName}-${idx}`;
                            return (
                                <li key={key} className="benefit-item">
                                    <span className="benefit-name">{item.benefitName}</span>
                                    <span className="benefit-meta">（数量{item.quantity}）</span>
                                    <span className="benefit-desc">：{item.remark || <Text type="secondary">&ndash;</Text>}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ))}
        </div>
    );
}

export default function OrganizationList() {
    const params = useParams();
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const dispatch = useDispatch();
    const filter = useSelector(state => state.conference.guest.filter);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const conferenceId = params?.id || 0;
    const [form] = Form.useForm();

    // 筛选项展开/收起
    const [expanded, setExpanded] = useState(true);

    // 筛选项本地模糊搜索（TreeSelect 需递归匹配子节点，使父节点随子节点命中而展示）
    const filterOptionByLabel = (input, option) => option?.label?.toLowerCase().includes(input?.toLowerCase());
    const filterTreeNodeByTitle = (input, treeNode) => {
        const searchText = input?.toLowerCase() || '';
        if (
            String(treeNode?.title || '')
                .toLowerCase()
                .includes(searchText)
        ) {
            return true;
        }
        return treeNode?.children?.some(child => filterTreeNodeByTitle(input, child));
    };

    // 数据
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [conference, _setConference] = useState(null);

    // 筛选项数据
    const [filterOptions, setFilterOptions] = useState({
        sponsors: [],
        customerManagers: [],
        halls: [],
        benefits: [],
        boothBenefits: [],
    });

    // 权益管理弹窗
    const [benefitDrawerOpen, setBenefitDrawerOpen] = useState(false);

    // 备注弹窗
    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [notesModalContent, setNotesModalContent] = useState('');

    // 新增/编辑机构权益抽屉
    const [visibleAddOrgsDrawer, setVisibleAddOrgsDrawer] = useState(false);
    const [currentOrg, setCurrentOrg] = useState(null);

    // 权益兑现弹窗
    const [realizationModalOpen, setRealizationModalOpen] = useState(false);
    const [realizationSponsorId, setRealizationSponsorId] = useState(null);

    // 导出会场权益弹窗
    const [exportHallModalOpen, setExportHallModalOpen] = useState(false);
    const [selectedHallIds, setSelectedHallIds] = useState([]);

    // 查看机构详情抽屉
    const [viewOrgDrawerOpen, setViewOrgDrawerOpen] = useState(false);
    const [detailOrgId, setDetailOrgId] = useState(null);

    // 会场规划图弹窗（manage-管理 / view-查看）
    const [hallPlanModalOpen, setHallPlanModalOpen] = useState(false);
    const [hallPlanMode, setHallPlanMode] = useState('manage');

    // 全屏
    const [isFullscreen, setIsFullscreen] = useState(false);

    // fixed-scroll 三件套
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    // 全屏相关
    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 初始化 pageSize 为 20
    const isPageSizeInit = useRef(false);
    useEffect(() => {
        if (!isPageSizeInit.current && filter.pageSize !== 20) {
            isPageSizeInit.current = true;
            dispatch(setFilter({ ...filter, pageSize: 20 }));
        }
    }, [dispatch, filter]);

    // 表单同步
    useEffect(() => {
        form.setFieldsValue(filter);
    }, [filter, form]);

    /**
     * 打开新增/编辑机构权益抽屉
     * @param {Object|null} item 编辑时传列表行数据，新增时传 null
     */
    const showAddOrgsDrawer = useCallback(item => {
        setCurrentOrg(item || null);
        setVisibleAddOrgsDrawer(true);
    }, []);

    const handleViewOrgDrawerOpenChange = useCallback(open => {
        setViewOrgDrawerOpen(open);
        if (!open) {
            setDetailOrgId(null);
        }
    }, []);

    const handleOpenOrgDetail = useCallback((e, orgId) => {
        e.stopPropagation();
        if (!orgId) return;
        setDetailOrgId(orgId);
        setViewOrgDrawerOpen(true);
    }, []);

    // 打开导出会场权益弹窗（默认选中全部会场）
    const openExportHallModal = () => {
        const allHallIds = (filterOptions?.halls || []).map(h => h.id);
        setSelectedHallIds(allHallIds);
        setExportHallModalOpen(true);
    };

    // 导出会场权益
    const handleExportHallBenefits = () => {
        const hallIds = selectedHallIds.join(',');
        window.location.href = `${config.API_PREFIX}/conference/boss/sponsor/exportHallBenefits?conferenceId=${conferenceId}&hallIds=${hallIds}`;
        setExportHallModalOpen(false);
    };

    // 导出机构权益
    const exportOrganizationBenefits = () => {
        window.location.href = `${config.API_PREFIX}/conference/boss/sponsor/exportBenefits?conferenceId=${conferenceId}`;
    };

    // 导出权益兑现明细
    const exportBenefitDetails = orgId => {
        window.location.href = `${config.API_PREFIX}/conference/boss/sponsor/exportSponsorBenefitDetail?conferenceId=${conferenceId}&orgId=${orgId}`;
    };

    // 获取会议项目信息
    useEffect(() => {
        if (!conferenceId) return;

        const fetchConferenceInfo = async () => {
            const res = await conferenceOnceApi({ id: Number(conferenceId) });
            if (res.status_code === 200) {
                _setConference(res.data);
            }
        };

        fetchConferenceInfo();
    }, [conferenceId]);

    // 会场规划图（后端返回字符串数组，元素为完整图片 URL；兼容历史的 JSON 数组字符串 / 逗号分隔字符串）
    const hallPlanImages = useMemo(() => {
        const raw = conference?.hallPlanImages;
        if (!raw) return [];
        let arr = raw;
        if (typeof arr === 'string') {
            try {
                arr = JSON.parse(arr);
            } catch {
                arr = arr.split(',');
            }
        }
        if (!Array.isArray(arr)) return [];
        return arr.filter(Boolean).map(url => (url.includes('http') ? url : config.OSS_PREFIX + url));
    }, [conference]);

    // 打开会场规划图弹窗
    const openHallPlanModal = mode => {
        setHallPlanMode(mode);
        setHallPlanModalOpen(true);
    };

    // 保存会场规划图
    const handleSaveHallPlan = async images => {
        const res = await conferenceUpdateHallPlanImagesApi({ id: Number(conferenceId), hallPlanImages: JSON.stringify(images || []) });
        if (res.status_code === 200) {
            message.success('保存成功');
            setHallPlanModalOpen(false);
            const latest = await conferenceOnceApi({ id: Number(conferenceId) });
            if (latest.status_code === 200) {
                _setConference(latest.data);
            }
        }
    };

    // 获取筛选项数据
    useEffect(() => {
        if (!conferenceId) return;

        const fetchFilterOptions = async () => {
            const res = await getSponsorFilterOptions({ conferenceId: Number(conferenceId) });
            if (res.status_code === 200) {
                const data = res.data || {};
                setFilterOptions({
                    sponsors: data.sponsors || [],
                    customerManagers: data.customerManagers || [],
                    halls: data.halls || [],
                    benefits: data.benefits || [],
                    boothBenefits: (data.benefits || []).filter(b => b.isBoothBenefit === 1),
                });
            }
        };

        fetchFilterOptions();
    }, [conferenceId]);

    // 获取列表数据
    const getOrgList = useCallback(async () => {
        setLoading(true);

        const param = {
            benefitIds: filter.benefitIds || [],
            benefitTypes: filter.benefitTypes || [],
            boothNumbers: filter.boothNumbers || [],
            conferenceId: Number(conferenceId),
            customerManagerPhones: filter.customerManagerPhones || [],
            hallIds: filter.hallIds || [],
            page: filter.page,
            pageSize: filter.pageSize,
            sortType: filter.sortType || 0,
            sponsorIds: filter.sponsorIds || [],
        };

        const res = await sponsorList(param);
        if (res.status_code === 200) {
            setData(res.data.list || []);
            setTotal(res.data.totalCount || 0);
        } else {
            setData([]);
            setTotal(0);
        }

        setLoading(false);
    }, [conferenceId, filter]);

    // 会议筛选项：按日期分组
    const hallTreeOptions = useMemo(() => {
        const groups = {};
        filterOptions.halls.forEach(hall => {
            const date = hall.dates || '其他';
            if (!groups[date]) {
                groups[date] = { title: date, value: date, key: date, children: [] };
            }
            groups[date].children.push({
                title: hall.name,
                value: hall.id,
                key: hall.id,
            });
        });
        return Object.values(groups);
    }, [filterOptions.halls]);

    // 展位编号筛选项：按权益名称分组
    const boothTreeOptions = useMemo(() => {
        const groups = {};
        filterOptions.boothBenefits.forEach(benefit => {
            const name = benefit.benefitName;
            if (!groups[name]) {
                groups[name] = { title: name, value: name, children: [] };
            }
            try {
                const booths = JSON.parse(benefit.boothNumbers);
                if (Array.isArray(booths)) {
                    booths.forEach(booth => {
                        groups[name].children.push({ title: booth, value: booth });
                    });
                }
            } catch {
                // ignore
            }
        });
        return Object.values(groups);
    }, [filterOptions.boothBenefits]);

    // 权益名称筛选项：按类型分组
    const benefitTreeOptions = useMemo(() => {
        const groups = {};
        filterOptions.benefits.forEach(benefit => {
            const typeLabel = BENEFIT_CATEGORY_MAP[benefit.type] || '其他';
            if (!groups[typeLabel]) {
                groups[typeLabel] = { title: typeLabel, value: typeLabel, children: [] };
            }
            groups[typeLabel].children.push({
                title: benefit.benefitName,
                value: benefit.benefitId,
            });
        });
        // 按 BENEFIT_CATEGORIES 顺序输出分组（与权益分类保持一致），过滤空分组
        return BENEFIT_CATEGORIES.map(cat => groups[cat.name]).filter(Boolean);
    }, [filterOptions.benefits]);

    useEffect(() => {
        if (conferenceId) {
            getOrgList();
        }
    }, [conferenceId, getOrgList]);

    const onFinish = values => {
        dispatch(
            setFilter({
                ...filter,
                ...values,
                page: 1,
            }),
        );
    };

    const onChangePage = (page, pageSize) => {
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };

    const onReset = () => {
        form.resetFields();
        setExpanded(false);
        dispatch(
            setFilter({
                page: 1,
                pageSize: 20,
                sortType: 1,
            }),
        );
    };

    // 排序切换
    const handleSortChange = ({ key }) => {
        dispatch(
            setFilter({
                ...filter,
                sortType: Number(key),
                page: 1,
            }),
        );
    };

    // 删除机构
    const handleDelete = useCallback(
        record => {
            Modal.confirm({
                title: '确认删除',
                content: `确定要删除机构「${record.name}」吗？删除后不可恢复。`,
                okText: '确认',
                cancelText: '取消',
                onOk: async () => {
                    const res = await delSponsor([record.id]);
                    if (res.status_code === 200) {
                        message.success('删除成功');
                        getOrgList();
                    } else {
                        message.error(res.message || '删除失败');
                    }
                },
            });
        },
        [getOrgList],
    );

    // 按钮列布局：6个筛选项时按钮在最后一格
    const buttonColLayout = useMemo(() => computeButtonColLayout(expanded ? 6 : 4), [expanded]);

    // 列定义
    const columns = useMemo(
        () => [
            {
                title: '机构及干系人',
                dataIndex: 'name',
                width: 240,
                fixed: 'left',
                render: (text, record) => (
                    <div className="org-name-cell">
                        <div className="org-logo-name">
                            <div className="org-name-row">
                                <span className="org-name">{text}</span>
                                {!!record.orgId && (
                                    <Tooltip title="查看机构详情">
                                        <span className="org-detail-btn" onClick={e => handleOpenOrgDetail(e, record.orgId)}>
                                            <i className="bi bi-box-arrow-up-right"></i>
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                            <span className="org-id">#{record.orgId}</span>
                        </div>
                        {!!record.title && (
                            <div className="org-designation">
                                <Tooltip title="名义">
                                    <Tag color="blue" variant="outline">
                                        {record.title}
                                    </Tag>
                                </Tooltip>
                            </div>
                        )}
                        {record.customerManagerName && (
                            <div className="org-contact">
                                <span className="contact-label">客户经理：</span>
                                {record.customerManagerName}
                            </div>
                        )}
                        {record.expertName && (
                            <div className="org-contact">
                                <span className="contact-label">机构客户：</span>
                                {record.expertName}
                            </div>
                        )}
                    </div>
                ),
            },
            ...BENEFIT_CATEGORIES.map(({ id, name }) => ({
                title: name,
                width: id === 4 ? 280 : 220,
                render: (_, record) => {
                    const items = (record.benefitList || []).filter(b => b.type === id);
                    return id === 4 ? <ForumBenefitContent items={items} /> : <BenefitContent items={items} />;
                },
            })),
            {
                title: '本机构在日程展示',
                dataIndex: 'hallList',
                width: 280,
                render: (_textArr, record) => {
                    return (
                        <div className="hall-list">
                            {record.hallNameList?.map(hall => {
                                // 如果有冒号分隔，前半部分显示黄色
                                const colonIndex = hall.indexOf('：');
                                if (colonIndex > 0) {
                                    const label = hall.slice(0, colonIndex + 1);
                                    const value = hall.slice(colonIndex + 1);
                                    return (
                                        <p key={hall}>
                                            <span className="hall-label">{label}</span>
                                            {value}
                                        </p>
                                    );
                                }
                                return <p key={hall}>{hall}</p>;
                            })}
                            {!record.hallNameList && <Text type="secondary">&ndash;</Text>}
                        </div>
                    );
                },
            },
            {
                title: '备注',
                dataIndex: 'notes',
                width: 100,
                render: text => {
                    if (!text) return <Text type="secondary">&ndash;</Text>;
                    return (
                        <span
                            className="notes-detail-link"
                            onClick={() => {
                                setNotesModalContent(text);
                                setNotesModalOpen(true);
                            }}
                        >
                            查看备注
                        </span>
                    );
                },
            },
            {
                title: '权益兑现情况',
                width: 100,
                render: (_, record) => {
                    const total = record.benefitTotalCount || 0;
                    const fulfilled = record.benefitRealizedCount || 0;
                    return `${total}/${fulfilled}`;
                },
            },
            {
                title: '操作',
                width: 200,
                fixed: 'right',
                render: (_, record) => {
                    const hasBenefits = (record.benefitList || []).length > 0;
                    return (
                        <span className="operation-btns">
                            <Button size="small" type="link" onClick={() => showAddOrgsDrawer(record)}>
                                编辑
                            </Button>
                            <Tooltip title={!hasBenefits ? '该机构暂无合同权益' : ''}>
                                <Button
                                    size="small"
                                    type="link"
                                    disabled={!hasBenefits}
                                    onClick={() => {
                                        setRealizationSponsorId(record.id);
                                        setRealizationModalOpen(true);
                                    }}
                                >
                                    权益兑现
                                </Button>
                            </Tooltip>
                            <Button size="small" type="link" disabled={!hasBenefits} onClick={() => exportBenefitDetails(record.orgId)}>
                                导出兑现明细
                            </Button>
                            <Button size="small" type="link" danger onClick={() => handleDelete(record)}>
                                删除
                            </Button>
                        </span>
                    );
                },
            },
        ],
        [handleDelete, handleOpenOrgDetail, showAddOrgsDrawer],
    );

    useEffect(() => {
        document.title = '机构权益-医会议';
    }, []);

    return (
        <div className="page-container organization-list-page">
            <PageHeader title="机构权益" breadcrumb={[{ label: '医会议', path: CONFERENCE_WORKSPACE_PARENT_PATH }, { label: conference?.name || '项目信息', path: `${CONFERENCE_WORKSPACE_ROUTE_BASE}/${conferenceId}` }, '机构权益管理']} {...filterToggleProps} />

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="organization-list-filter"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <Form {...filterFormItemLayout} form={form} onFinish={onFinish} autoComplete="off" requiredMark={false}>
                        <Row gutter={[16, 16]}>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="机构名称" name="sponsorIds" style={filterFormItemStyle}>
                                    <Select mode="multiple" maxTagCount="responsive" placeholder="请选择机构" allowClear showSearch optionFilterProp="label" filterOption={filterOptionByLabel} options={filterOptions.sponsors.map(s => ({ label: s.name, value: s.id }))} />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="客户经理" name="customerManagerPhones" style={filterFormItemStyle}>
                                    <Select mode="multiple" maxTagCount="responsive" placeholder="请选择客户经理" allowClear showSearch optionFilterProp="label" filterOption={filterOptionByLabel} options={filterOptions.customerManagers.map(c => ({ label: c.name, value: c.phone }))} />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="展位编号" name="boothNumbers" style={filterFormItemStyle}>
                                    <TreeSelect multiple treeData={boothTreeOptions} filterTreeNode={filterTreeNodeByTitle} placeholder="请选择展位编号" allowClear treeCheckable showCheckedStrategy="SHOW_CHILD" showSearch treeNodeFilterProp="title" treeDefaultExpandAll style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="会议" name="hallIds" style={filterFormItemStyle}>
                                    <TreeSelect multiple treeData={hallTreeOptions} filterTreeNode={filterTreeNodeByTitle} placeholder="请选择会议" allowClear treeCheckable showCheckedStrategy="SHOW_CHILD" showSearch treeNodeFilterProp="title" treeDefaultExpandAll style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>

                            {expanded && (
                                <>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="权益分类" name="benefitTypes" style={filterFormItemStyle}>
                                            <Select mode="multiple" maxTagCount="responsive" options={BENEFIT_TYPE_OPTIONS} placeholder="请选择权益分类" allowClear showSearch optionFilterProp="label" filterOption={filterOptionByLabel} />
                                        </Form.Item>
                                    </Col>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="权益名称" name="benefitIds" style={filterFormItemStyle}>
                                            <TreeSelect multiple treeData={benefitTreeOptions} filterTreeNode={filterTreeNodeByTitle} placeholder="请选择权益名称" allowClear treeCheckable showCheckedStrategy="SHOW_CHILD" showSearch treeNodeFilterProp="title" treeDefaultExpandAll style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                </>
                            )}

                            <Col {...buttonColLayout}>
                                <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                    <Space size="small">
                                        <Button type="primary" icon={<i className="bi bi-search" />} htmlType="submit">
                                            检索
                                        </Button>
                                        <Button type="default" onClick={onReset}>
                                            重置
                                        </Button>
                                        <Button
                                            type="link"
                                            size="small"
                                            aria-expanded={expanded}
                                            onClick={() => {
                                                setExpanded(prev => {
                                                    if (prev) {
                                                        form.setFieldsValue({
                                                            benefitTypes: undefined,
                                                            benefitIds: undefined,
                                                        });
                                                    }
                                                    return !prev;
                                                });
                                            }}
                                            style={{ fontSize: 12, color: token.colorTextSecondary }}
                                        >
                                            {expanded ? (
                                                <>
                                                    收起 <span className="bi bi-chevron-up" aria-hidden="true" />
                                                </>
                                            ) : (
                                                <>
                                                    展开 <span className="bi bi-chevron-down" aria-hidden="true" />
                                                </>
                                            )}
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="organization-list-table-region">
                <div className="main-toolbar organization-list-toolbar">
                    <div className="main-tools">
                        <Space align="center" size="middle">
                            <Button type="primary" onClick={() => showAddOrgsDrawer(null)}>
                                新增机构权益
                            </Button>
                            <span className="benefit-deadline">
                                <b>权益提交截止时间：</b>
                                {conference?.benefitCollectionDeadline ? dayjs(conference.benefitCollectionDeadline).format('YYYY年M月D日 HH:mm') : <span style={{ color: token.colorWarning }}>暂未设置</span>}
                                <Tooltip title="前往设置">
                                    <Button type="link" size="small" onClick={() => navigate(workspaceConferenceInfoPath(conferenceId))} aria-label="设置权益提交截止日期">
                                        <i className="bi bi-pencil" aria-hidden="true" />
                                    </Button>
                                </Tooltip>
                            </span>
                            <span className="hall-plan-images-entry">
                                <b>会场规划图：</b>
                                {hallPlanImages.length > 0 ? (
                                    <Button type="link" size="small" style={{ paddingInline: 0 }} onClick={() => openHallPlanModal('view')} aria-label="查看会场规划图">
                                        查看
                                    </Button>
                                ) : (
                                    <span style={{ color: token.colorError }}>未上传</span>
                                )}
                                <Tooltip title={hallPlanImages.length > 0 ? '编辑' : '上传'}>
                                    <Button type="link" size="small" onClick={() => openHallPlanModal('manage')} aria-label="管理会场规划图">
                                        <i className="bi bi-pencil" aria-hidden="true" />
                                    </Button>
                                </Tooltip>
                            </span>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Button type="default" onClick={() => setBenefitDrawerOpen(true)}>
                                权益管理
                            </Button>
                            <Button type="default" onClick={() => openExportHallModal()}>
                                导出会场权益
                            </Button>
                            <Button type="default" onClick={() => exportOrganizationBenefits()}>
                                导出机构权益
                            </Button>
                            <Dropdown
                                menu={{
                                    items: SORT_OPTIONS.map(opt => ({
                                        key: opt.key,
                                        label: <span style={{ color: filter.sortType === Number(opt.key) ? 'var(--ant-color-primary)' : undefined }}>{opt.label}</span>,
                                    })),
                                    onClick: handleSortChange,
                                    selectedKeys: [String(filter.sortType || 1)],
                                }}
                            >
                                <Button type="default" icon={<span className="bi bi-sort-down" aria-hidden="true" />} aria-label="排序方式" />
                            </Dropdown>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={getOrgList} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <Table
                    className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                    rowKey="id"
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    bordered
                    size="medium"
                    sticky
                    pagination={{
                        total,
                        showTotal: () => `共${total}条`,
                        current: filter.page,
                        pageSize: filter.pageSize,
                        pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                        onChange: onChangePage,
                        showQuickJumper: true,
                        showSizeChanger: { placement: 'topRight' },
                    }}
                    scroll={{
                        x: 3000,
                        ...(tableScrollY != null ? { y: tableScrollY } : {}),
                    }}
                />
            </div>

            {/* 导出会场权益弹窗 */}
            <Modal title="会场权益导出" open={exportHallModalOpen} onCancel={() => setExportHallModalOpen(false)} onOk={handleExportHallBenefits} okText="导出数据" cancelText="取消" width={560}>
                <div style={{ marginBottom: 12 }}>选择要导出的会场权益</div>
                <Tree checkable treeData={hallTreeOptions} defaultExpandAll checkedKeys={selectedHallIds} onCheck={keys => setSelectedHallIds(keys.filter(key => !Number.isNaN(Number(key))))} />
            </Modal>

            {/* 权益管理弹窗 */}
            <BenefitManageDrawer open={benefitDrawerOpen} onClose={() => setBenefitDrawerOpen(false)} conferenceId={conferenceId} />

            {/* 会场规划图弹窗 */}
            <HallPlanImageModal open={hallPlanModalOpen} mode={hallPlanMode} images={hallPlanImages} onCancel={() => setHallPlanModalOpen(false)} onSave={handleSaveHallPlan} />

            {/* 备注弹窗 */}
            <Modal title="备注" open={notesModalOpen} onCancel={() => setNotesModalOpen(false)} footer={null} width={600}>
                <div style={{ maxHeight: 480, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{notesModalContent}</div>
            </Modal>

            {/* 新增/编辑机构权益抽屉 */}
            <AddOrganizationDrawer visibleAddOrgsDrawer={visibleAddOrgsDrawer} setVisibleAddOrgsDrawer={setVisibleAddOrgsDrawer} sponsorList={getOrgList} organization={currentOrg} setOrganization={setCurrentOrg} />

            {/* 权益兑现弹窗 */}
            <BenefitRealizationModal
                open={realizationModalOpen}
                sponsorId={realizationSponsorId}
                onClose={() => {
                    setRealizationModalOpen(false);
                    setRealizationSponsorId(null);
                }}
                onSuccess={() => {
                    getOrgList();
                }}
            />

            {/* 查看机构详情抽屉 */}
            <span onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                <Suspense fallback={<Spin />}>
                    <ViewOrgDrawer openViewOrgDrawer={viewOrgDrawerOpen} setOpenViewOrgDrawer={handleViewOrgDrawerOpenChange} id={detailOrgId} setCurrentRecordId={setDetailOrgId} setOpenAddOrgDrawer={() => {}} showEditButton={false} getOrganizationsList={() => {}} />
                </Suspense>
            </span>
        </div>
    );
}
